import React, {
	type ReactNode,
	useState,
	useEffect,
	useRef,
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
} from 'react';
import noop from 'lodash/noop';
import { graphql, useFragment } from 'react-relay';
import Avatar from '@atlaskit/avatar';
import { MenuGroup, LinkItem, Section } from '@atlaskit/menu';
import type { TriggerProps } from '@atlaskit/popup';
import {
	withConnectHost,
	isConnectDialogWebItem,
} from '@atlassian/jira-connect-utils/src/common/utils/ecosystem-connect-host.tsx';
import { useNavigationItemAnalytics } from '@atlassian/jira-horizontal-nav-common/src/common/analytics.tsx';
import { useTabShortcuts } from '@atlassian/jira-horizontal-nav-common/src/common/tab-shortcut-context/index.tsx';
import { V4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getNav4Rollout } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-nav4-rollout/index.tsx';
import { useTabContext } from '@atlassian/navigation-kit-ui-tab';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { AppTabMenu_tab$key } from './__generated__/AppTabMenu_tab.graphql';
import type { AppTabMenu_container$key } from './__generated__/AppTabMenu_container.graphql';
import { TabWithMenu } from './TabWithMenu.tsx';

export type Props = {
	tabFragment: AppTabMenu_tab$key;
	containerFragment: AppTabMenu_container$key;
	icon: ReactNode;
	testId?: string;
	position?: number | undefined;
	elemAfter?: ReactNode;
	onClick?: (() => void) | undefined;
	beforeItemId: string | null;
	afterItemId: string | null;
};

export const AppTabMenu = ({
	tabFragment,
	containerFragment,
	position,
	icon,
	testId,
	elemAfter,
	beforeItemId,
	afterItemId,
	onClick,
}: Props): React.JSX.Element | null => {
	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(false);
	const { isOverflowing } = useTabContext();

	const { registerTab } = useTabShortcuts();
	const webItemRefs = useRef<Array<Array<HTMLElement>>>([]);

	const tabData = useFragment(
		graphql`
			fragment AppTabMenu_tab on JiraAppNavigationItem {
				...TabWithMenu_tab
				... on JiraAppNavigationItem {
					appId
					label
					__typename
					sections {
						label
						links {
							label
							iconUrl
							url
							styleClass
						}
					}
				}
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment AppTabMenu_container on JiraContainerNavigationResult {
				...TabWithMenu_container
			}
		`,
		containerFragment,
	);

	useEffect(() => {
		if (tabData.label) {
			const deregisterTab = registerTab({
				index: position,
				callback: () => {
					setIsDropdownMenuOpen((isDropdownMenuCurrent) => !isDropdownMenuCurrent);
				},
				label: tabData.label,
			});

			return deregisterTab;
		}
		return noop;
	}, [registerTab, tabData.label, position]);

	const triggerAnalytics = useNavigationItemAnalytics(
		useMemo(
			() => ({
				itemType: 'appSubpage',
				itemId: tabData.appId ?? '',
				navState: V4,
				stage: getNav4Rollout().stage,
				navigationLayer: CONTAINER,
				navigationContainer: TAB,
				isInMore: false,
			}),
			[tabData.appId],
		),
	);

	if (!tabData.label) {
		return null;
	}

	const handleClick = () => {
		setIsDropdownMenuOpen((isDropdownMenuCurrent) => !isDropdownMenuCurrent);
		onClick?.();
	};

	const handleConnectItemClick =
		(
			className: string | undefined | null,
			href: string | undefined | null,
			sectionIndex: number,
			index: number,
		) =>
		(e: KeyboardEvent | MouseEvent) => {
			if (className && href) {
				withConnectHost((connectHost) => {
					connectHost.triggerWebItem(webItemRefs.current[sectionIndex][index], className, href);
				});

				if (isConnectDialogWebItem(className)) {
					e.preventDefault();
					e.stopPropagation();
				}
			}

			triggerAnalytics();
		};

	const trigger = (triggerProps: TriggerProps) => (
		<TabWithMenu
			{...triggerProps}
			type="dropdown-trigger"
			isOpen={isDropdownMenuOpen}
			onClick={handleClick}
			elemBefore={icon}
			position={position}
			tabFragment={tabData}
			containerFragment={containerData}
			elemAfter={elemAfter}
			testId={testId}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
		/>
	);

	return (
		<Popup
			trigger={trigger}
			placement={isOverflowing ? 'left' : 'bottom-end'}
			messageId="horizontal-nav-tabs.ui.popup"
			messageType="transactional"
			shouldFlip={false}
			isOpen={isDropdownMenuOpen}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="horizontal-nav-app-tab.ui.app-menu.dropdown-menu"
			onClose={() => setIsDropdownMenuOpen((isDropdownMenuCurrent) => !isDropdownMenuCurrent)}
			content={() => (
				<MenuGroup>
					{tabData?.sections &&
						tabData?.sections.map((section, i) => {
							if (!webItemRefs.current[i])
								webItemRefs.current[i] = new Array(section?.links?.length ?? 0);
							return (
								<Section
									key={section?.label ?? `section-${i}`}
									title={section?.label ?? undefined}
									hasSeparator={i !== 0}
								>
									{(section?.links ?? [])
										.filter((link) => link && link.label)
										.map((link, j) => (
											<LinkItem
												key={link?.label ?? `link-${j}`}
												href={link?.url ?? ''}
												isDisabled={!link?.url}
												iconBefore={
													link?.iconUrl ? <Avatar src={link.iconUrl} size="small" /> : undefined
												}
												onClick={handleConnectItemClick(link?.styleClass, link?.url, i, j)}
												ref={(ref) => {
													if (ref !== null) webItemRefs.current[i][j] = ref;
												}}
											>
												{link?.label}
											</LinkItem>
										))}
								</Section>
							);
						})}
				</MenuGroup>
			)}
		/>
	);
};
